'use client'

/**
 * The client command coordinator (Issue #12949).
 *
 * One component owns everything that happens on the browser side of a
 * capability: the confirmation prompt, the fetch that executes a confirmed
 * action, the reconciliation of the visible application, and the polite
 * announcement. It renders nothing except a live region and — while one is
 * pending — the app's established `ConfirmationDialog`.
 *
 * ## Why the coordinator lives inside the chat panel
 *
 * The panel is where an outcome arrives (as an SSE frame) and where the user
 * is looking when they are asked to approve something. Mounting the
 * coordinator in the root layout instead would put the dialog arbitrarily far
 * from the conversation that proposed it, and would keep a confirmation alive
 * after the user closed the chat — which is the one case where "the user
 * walked away" should mean "nothing happens".
 *
 * ## Why confirming re-posts everything
 *
 * `confirmPendingAction` sends the capability, the target, the input AND a
 * FRESH workspace snapshot to `POST /api/ai-chat/workspace-action`, which runs
 * the same checks the in-stream path did. Nothing is remembered between the
 * proposal and the approval, on either side. That is not a shortcut: a fresh
 * snapshot at confirm time is a strictly better staleness signal than a stored
 * token, because it reflects where the user is NOW, and the window between
 * proposing and approving is exactly when they are free to navigate away.
 *
 * The client checks `hasRegisteredAction` before sending, so a surface that
 * unmounted while the dialog was open fails locally and instantly; the server
 * checks it again over the snapshot it is sent, because the client's check is
 * a courtesy and the server's is the rule.
 */

import { useCallback, useEffect, useId, useRef, useState } from 'react'
import { useRouter } from 'next/navigation'
import { ConfirmationDialog } from '@/components/confirmation-dialog'
import {
  AlertCircle,
  EyeOff,
  Lock,
  Paperclip,
  Target,
  User,
  X,
} from '@/components/icons'
import { Button } from '@/components/ui/button'
import { useWorkspaceContextRegistry } from '@/contexts/workspace-context'
import {
  planWorkspaceOutcome,
  workspaceEntitySelector,
} from '@/lib/workspace-actions/outcome'
import { NOTE_CHAT_FILE_STATUS_TEXT } from '@/lib/notes/note-chat-files'
import {
  pendingFileStatuses,
  withoutPendingFile,
} from '@/lib/workspace-actions/pending-files'
import {
  noteSubjectsPrivacyLine,
  withoutPendingSubject,
} from '@/lib/workspace-actions/pending-subjects'
import type {
  WorkspaceActionOutcome,
  WorkspacePendingAction,
  WorkspacePendingSubject,
} from '@/lib/workspace-actions/types'
import { reportClientError } from '@/lib/client-error'
import { ERROR_IDS } from '@/lib/errors/constants'

/** Where a confirmed capability is executed. */
export const WORKSPACE_ACTION_ENDPOINT = '/api/ai-chat/workspace-action'

/** Said when the surface that offered the action is no longer on screen. */
export const STALE_ACTION_MESSAGE =
  'That is no longer available on this screen, so nothing was changed.'

/**
 * Said when the request itself did not complete.
 *
 * Deliberately does NOT say "nothing was changed", because at this point we do
 * not know. A timeout or a dropped response can happen after the server has
 * committed, and the two are indistinguishable from here — so the earlier
 * wording was a promise the client is not in a position to make. Refusals,
 * which DO know, keep saying it: they arrive as a 200 carrying a `rejected`
 * envelope and never reach this constant.
 */
export const TRANSPORT_FAILURE_MESSAGE =
  'That action could not be completed. Check whether it went through before ' +
  'trying again.'

/** Said when the user declines. */
export const CANCELLED_MESSAGE = 'Cancelled. Nothing was changed.'

export interface WorkspaceActionCoordinator {
  pending: WorkspacePendingAction | null
  isExecuting: boolean
  announcement: string
  /** Feed an outcome in — from the chat stream, or from a confirmation. */
  handleOutcome: (outcome: WorkspaceActionOutcome) => void
  confirmPendingAction: () => Promise<void>
  cancelPendingAction: () => void
  /**
   * Take one proposed subject off a pending note action (#16110), from what
   * the dialog shows and from the input the confirm posts.
   */
  removePendingSubject: (
    subject: Pick<WorkspacePendingSubject, 'kind' | 'id'>,
  ) => void
  /**
   * Take one of the chat's files off a pending note action (#16111), from
   * what the dialog shows and from the `attachmentIds` the confirm posts.
   */
  removePendingFile: (fileId: string) => void
}

/** Which chat surface hosts the coordinator (#16109). */
export type WorkspaceChatSurface = 'home' | 'panel'

export interface WorkspaceActionCoordinatorOptions {
  /**
   * Where the chat is (#16109): Home's in-page conversation or the docked
   * panel. Posted with a confirmation, for the adoption event only.
   */
  surface?: WorkspaceChatSurface
  /**
   * Called with the chat's reply — the outcome's message and a link — when a
   * confirmed capability made a record (#16109: "Created the note … Open the
   * note"). The server persists the same text on the conversation, so the
   * caller only has to show it.
   */
  onChatReply?: (markdown: string) => void
}

export function useWorkspaceActionCoordinator(
  options: WorkspaceActionCoordinatorOptions = {},
): WorkspaceActionCoordinator {
  const router = useRouter()
  // Through refs, so a caller passing an inline arrow or a surface that flips
  // with Home's mount does not change the identity of every callback below.
  const surfaceRef = useRef<WorkspaceChatSurface>(options.surface ?? 'panel')
  surfaceRef.current = options.surface ?? 'panel'
  const onChatReplyRef = useRef(options.onChatReply)
  onChatReplyRef.current = options.onChatReply
  const registry = useWorkspaceContextRegistry()
  const [pending, setPendingState] = useState<WorkspacePendingAction | null>(
    null,
  )
  const [isExecuting, setIsExecuting] = useState(false)
  const [announcement, setAnnouncement] = useState('')
  // Mirrors `pending` for the "is one already up" test in `handleOutcome`.
  // See the comment there for why the state itself is not read.
  const pendingRef = useRef<WorkspacePendingAction | null>(null)

  /** Keeps `pendingRef` in step with the state, so the two cannot drift. */
  const setPending = useCallback((next: WorkspacePendingAction | null) => {
    pendingRef.current = next
    setPendingState(next)
  }, [])
  // Announcements are re-announced when the TEXT changes; two identical
  // messages in a row would otherwise be silent. A counter suffix would be
  // read aloud, so the region is cleared first instead.
  const announceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const announce = useCallback((message: string) => {
    if (announceTimerRef.current) clearTimeout(announceTimerRef.current)
    setAnnouncement('')
    announceTimerRef.current = setTimeout(() => setAnnouncement(message), 0)
  }, [])

  // Closing the chat panel while an announcement is queued would otherwise
  // leave a timer that calls `setAnnouncement` on an unmounted component —
  // `announce` clears the previous timer, but only when a NEXT one arrives.
  // `ConfirmationDialog` does the same for its own focus timer.
  useEffect(
    () => () => {
      if (announceTimerRef.current) clearTimeout(announceTimerRef.current)
    },
    [],
  )

  const applyOutcome = useCallback(
    (outcome: WorkspaceActionOutcome) => {
      const plan = planWorkspaceOutcome(outcome)

      // Surfaces holding local state hear about it first, so a card that can
      // reconcile itself does so before (or instead of) a route refetch.
      //
      // Gated on the PLAN rather than fired unconditionally. `changedEntityIds`
      // is empty on anything but a success, which is the same asymmetry the
      // navigate/refresh/focus arms below have and the one WORKSPACE_ACTIONS.md
      // § Synchronizing states: a refusal changed nothing on the server, so
      // nothing about the screen should move. Broadcasting a `rejected` outcome
      // is inert only while nothing subscribes — the first surface that
      // reconciles from `changedEntities` without re-checking `status` would
      // move the screen on a refusal.
      if (plan.changedEntityIds.length > 0) {
        registry.publishActionOutcome(outcome)
      }

      if (plan.navigateTo) router.push(plan.navigateTo)
      if (plan.refreshRoute) router.refresh()

      if (plan.focusEntityId) {
        // After the refetched markup lands. `requestAnimationFrame` rather
        // than a timeout so this rides the next paint, and a missing element
        // is a no-op: the record may simply not be on this screen.
        const selector = workspaceEntitySelector(plan.focusEntityId)
        requestAnimationFrame(() => {
          const element = document.querySelector<HTMLElement>(selector)
          element?.focus?.()
        })
      }

      // #16109 — the chat's reply, linking the record without leaving the
      // page. Only ever on a success: `chatReply` is unset otherwise.
      if (plan.chatReply) onChatReplyRef.current?.(plan.chatReply)

      announce(plan.announcement)
    },
    [registry, router, announce],
  )

  const handleOutcome = useCallback(
    (outcome: WorkspaceActionOutcome) => {
      if (outcome.status === 'confirmation_required' && outcome.pending) {
        // A second proposal while one is pending is REFUSED, not queued and
        // not swapped in. The chat route runs up to MAX_TOOL_FOLLOW_UPS tool
        // calls per turn, so a model that proposes two writes emits two
        // frames; a bare `setPending` would show the user the second dialog
        // and drop the first silently, while the transcript still claims both
        // are awaiting approval. `tool.ts`'s prompt section tells the model
        // one at a time, because the model is the party that has to adapt.
        //
        // Read through a REF rather than the `pending` state: putting
        // `pending` in this callback's deps would change its identity on every
        // proposal, and deciding inside a `setPending` updater would run the
        // announcement twice under StrictMode's double-invoke.
        if (pendingRef.current) return
        setPending(outcome.pending)
        // Announced as well as shown: the dialog takes focus, but a screen
        // reader user who dismissed it should still know what was proposed.
        announce(outcome.message)
        return
      }
      setPending(null)
      applyOutcome(outcome)
    },
    [applyOutcome, announce, setPending],
  )

  const cancelPendingAction = useCallback(() => {
    setPending(null)
    announce(CANCELLED_MESSAGE)
  }, [announce, setPending])

  const removePendingSubject = useCallback(
    (subject: Pick<WorkspacePendingSubject, 'kind' | 'id'>) => {
      // Not while the confirm is in flight: the request already left with
      // the list it had.
      if (!pendingRef.current || isExecuting) return
      setPending(withoutPendingSubject(pendingRef.current, subject))
    },
    [isExecuting, setPending],
  )

  const removePendingFile = useCallback(
    (fileId: string) => {
      // Not while the confirm is in flight, as for a subject.
      if (!pendingRef.current || isExecuting) return
      setPending(withoutPendingFile(pendingRef.current, fileId))
    },
    [isExecuting, setPending],
  )

  const confirmPendingAction = useCallback(async () => {
    if (!pending || isExecuting) return

    // The client half of the staleness rule — see this file's header.
    if (!registry.hasRegisteredAction(pending.capability, pending.targetId)) {
      setPending(null)
      announce(STALE_ACTION_MESSAGE)
      return
    }

    setIsExecuting(true)
    try {
      const response = await fetch(WORKSPACE_ACTION_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          capability: pending.capability,
          targetId: pending.targetId,
          input: pending.input,
          // FRESH, not the snapshot the proposal was made against.
          workspaceContext: registry.getWorkspaceContext(),
          // #16109 — the proposing conversation (re-checked as the session
          // person's own on the server) and where the chat is.
          ...(pending.conversationId
            ? { conversationId: pending.conversationId }
            : {}),
          surface: surfaceRef.current,
        }),
      })

      if (!response.ok) {
        setPending(null)
        announce(TRANSPORT_FAILURE_MESSAGE)
        return
      }

      const outcome = (await response.json()) as WorkspaceActionOutcome
      setPending(null)
      applyOutcome(outcome)
    } catch (error) {
      setPending(null)
      announce(TRANSPORT_FAILURE_MESSAGE)
      reportClientError({
        errorId: ERROR_IDS.WORKSPACE_ACTION_EXECUTE_FAILED,
        action: 'confirmPendingAction',
        error,
        context: { capability: pending.capability },
      })
    } finally {
      setIsExecuting(false)
    }
  }, [pending, isExecuting, registry, applyOutcome, announce, setPending])

  return {
    pending,
    isExecuting,
    announcement,
    handleOutcome,
    confirmPendingAction,
    cancelPendingAction,
    removePendingSubject,
    removePendingFile,
  }
}

/**
 * The rendered half: the confirmation prompt and the live region.
 *
 * `confirmVariant="default"` rather than `danger`: nothing in the initial
 * capability set destroys anything (AC6), and dressing a task creation in the
 * red destructive treatment would spend the signal that the first genuinely
 * destructive capability will need.
 */
export function WorkspaceActionHost({
  coordinator,
  isTurnStreaming = false,
}: {
  coordinator: WorkspaceActionCoordinator
  /**
   * The proposing turn is still streaming (#16109). Confirming is held until
   * it ends: the chat persists a turn's messages only when it finishes, and
   * the send loop streams into the LAST message — so a reply written mid-turn
   * would land ahead of the request on reload and cut the turn off on screen.
   */
  isTurnStreaming?: boolean
}) {
  const { pending, isExecuting, announcement } = coordinator

  return (
    <>
      <div
        role="status"
        aria-live="polite"
        className="sr-only"
        data-testid="workspace-action-announcer"
      >
        {announcement}
      </div>
      {pending ? (
        <ConfirmationDialog
          isOpen
          onClose={coordinator.cancelPendingAction}
          onConfirm={coordinator.confirmPendingAction}
          title={pending.title}
          description={pending.description}
          confirmText="Yes, do it"
          cancelText="No, cancel"
          confirmVariant="default"
          isLoading={isExecuting}
          confirmDisabled={isTurnStreaming}
          // The subject chips' and files' Remove buttons are edits, not the
          // question: open on Cancel so an Enter never silently drops one.
          initialFocus={pending.subjects || pending.files ? 'cancel' : 'auto'}
        >
          {pending.preview || pending.audience ? (
            <PendingActionDetail
              preview={pending.preview}
              audience={pending.audience}
            />
          ) : null}
          {pending.subjects ? (
            <PendingNoteSubjects
              subjects={pending.subjects}
              disabled={isExecuting}
              onRemove={coordinator.removePendingSubject}
            />
          ) : null}
          {pending.files ? (
            <PendingNoteFiles
              pending={pending}
              disabled={isExecuting}
              onRemove={coordinator.removePendingFile}
            />
          ) : null}
          {isTurnStreaming ? (
            <p
              data-testid="workspace-action-waiting"
              className="text-sm text-text-secondary"
            >
              Waiting for the reply to finish…
            </p>
          ) : null}
        </ConfirmationDialog>
      ) : null}
    </>
  )
}

/**
 * What a write will say and who will read it (#16109), under the dialog's
 * description: the first lines of the content, as plain text, and the
 * audience with a lock. Rendered as React text, never as markup — the
 * content is model-written.
 */
function PendingActionDetail({
  preview,
  audience,
}: {
  preview?: string
  audience?: string
}) {
  return (
    <div className="flex flex-col gap-2">
      {preview ? (
        <blockquote
          data-testid="workspace-action-preview"
          // Scrolls past ~6 lines, so it takes focus: keyboard users scroll it
          // too (axe `scrollable-region-focusable`).
          tabIndex={0}
          aria-label="What will be written"
          className="max-h-40 overflow-y-auto whitespace-pre-line border-l-2 border-stroke-regular pl-3 text-sm text-text-secondary"
        >
          {preview}
        </blockquote>
      ) : null}
      {audience ? (
        <p className="inline-flex items-center gap-1.5 text-sm text-text-secondary">
          <Lock className="h-4 w-4" aria-hidden="true" />
          <span data-testid="workspace-action-audience">{audience}</span>
        </p>
      ) : null}
    </div>
  )
}

/**
 * Who and what the note will be about (#16110): "About:" and a chip per
 * subject, each with its own Remove button, and the sentence that says the
 * people linked can't see the note — linking never shares it.
 *
 * Removing the focused chip's button would drop focus to the page, outside
 * the dialog, so focus moves to the next Remove button, or to the section
 * itself when it was the last one but the section stays; with nothing left
 * the section unmounts and focus goes to the region that wraps it.
 */
function PendingNoteSubjects({
  subjects,
  disabled,
  onRemove,
}: {
  subjects: readonly WorkspacePendingSubject[]
  disabled: boolean
  onRemove: WorkspaceActionCoordinator['removePendingSubject']
}) {
  const regionRef = useRef<HTMLDivElement>(null)
  const focusAfterRemoval = useRef<number | null>(null)

  useEffect(() => {
    const index = focusAfterRemoval.current
    if (index === null) return
    focusAfterRemoval.current = null
    const region = regionRef.current
    const buttons =
      region?.querySelectorAll<HTMLButtonElement>(
        '[data-note-subject-remove]',
      ) ?? []
    const next = buttons[Math.min(index, buttons.length - 1)]
    if (next) next.focus()
    else region?.focus()
  })

  const privacy = noteSubjectsPrivacyLine(subjects)
  const labelId = useId()

  return (
    <div
      ref={regionRef}
      tabIndex={-1}
      className="rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-stroke-selected"
    >
      {subjects.length > 0 ? (
        <section
          data-testid="workspace-action-note-subjects"
          aria-labelledby={labelId}
          className="mt-3 flex flex-col gap-2"
        >
          <div className="flex flex-wrap items-center gap-2">
            <h3
              id={labelId}
              className="text-sm font-medium text-text-secondary"
            >
              About:
            </h3>
            <ul
              className="flex flex-wrap items-center gap-2"
              aria-label="People and priorities this note will be about"
            >
              {subjects.map((subject, index) => (
                <li
                  key={`${subject.kind}:${subject.id}`}
                  data-testid={`workspace-action-note-subject-${subject.kind}`}
                  className="inline-flex items-center gap-2 rounded-full border border-stroke-light bg-surface-elevated py-1 pr-1 pl-3 text-sm text-text-primary"
                >
                  {subject.kind === 'person' ? (
                    <User className="h-4 w-4 shrink-0" aria-hidden="true" />
                  ) : (
                    <Target className="h-4 w-4 shrink-0" aria-hidden="true" />
                  )}
                  <span className="font-medium">{subject.label}</span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="pointer-coarse:min-h-11 pointer-coarse:min-w-11 shrink-0 rounded-full"
                    aria-label={`Remove ${subject.label}`}
                    data-note-subject-remove=""
                    disabled={disabled}
                    onClick={() => {
                      focusAfterRemoval.current = index
                      onRemove(subject)
                    }}
                  >
                    <X className="h-4 w-4" aria-hidden="true" />
                  </Button>
                </li>
              ))}
            </ul>
          </div>
          {privacy ? (
            <p className="inline-flex items-center gap-1.5 text-sm text-text-secondary">
              <EyeOff className="h-4 w-4 shrink-0" aria-hidden="true" />
              <span data-testid="workspace-action-note-subjects-privacy">
                {privacy}
              </span>
            </p>
          ) : null}
        </section>
      ) : (
        // Every subject removed: say so, so the focus the last removal
        // hands this region lands on something visible, not an empty box.
        <p
          data-testid="workspace-action-note-subjects-none"
          className="mt-3 text-sm text-text-secondary"
        >
          This note won’t be about anyone or any priority.
        </p>
      )}
    </div>
  )
}

/**
 * The chat's files the note will save (#16111): "Files:" and a row per file
 * — its kind, its name, and, for one the note won't take, "Won’t be added"
 * and why — each with its own Remove button. Said before the person
 * confirms, so nothing is dropped silently; removing a file frees its slot,
 * and the statuses are recomputed from what is left.
 *
 * Focus after a removal follows the subjects' rule: the next Remove button,
 * else this region, which then says no files will be saved.
 */
function PendingNoteFiles({
  pending,
  disabled,
  onRemove,
}: {
  pending: WorkspacePendingAction
  disabled: boolean
  onRemove: WorkspaceActionCoordinator['removePendingFile']
}) {
  const regionRef = useRef<HTMLDivElement>(null)
  const focusAfterRemoval = useRef<number | null>(null)

  useEffect(() => {
    const index = focusAfterRemoval.current
    if (index === null) return
    focusAfterRemoval.current = null
    const region = regionRef.current
    const buttons =
      region?.querySelectorAll<HTMLButtonElement>('[data-note-file-remove]') ??
      []
    const next = buttons[Math.min(index, buttons.length - 1)]
    if (next) next.focus()
    else region?.focus()
  })

  const entries = pendingFileStatuses(pending)
  const labelId = useId()

  return (
    <div
      ref={regionRef}
      tabIndex={-1}
      className="rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-stroke-selected"
    >
      {entries.length > 0 ? (
        <section
          data-testid="workspace-action-note-files"
          aria-labelledby={labelId}
          className="mt-3 flex flex-col gap-2"
        >
          <h3 id={labelId} className="text-sm font-medium text-text-secondary">
            Files:
          </h3>
          <ul
            className="flex flex-col gap-2"
            aria-label="Files from this chat to save with the note"
          >
            {entries.map(({ file, status }, index) => (
              <li
                key={file.id}
                data-testid="workspace-action-note-file"
                data-status={status}
                className="flex items-center gap-2 rounded-lg border border-stroke-light bg-surface-elevated py-1 pr-1 pl-3 text-sm text-text-primary"
              >
                <Paperclip className="h-4 w-4 shrink-0" aria-hidden="true" />
                <span className="flex min-w-0 flex-1 flex-col">
                  <span className="truncate font-medium">{file.fileName}</span>
                  <span className="text-text-secondary">
                    {status === 'add' ? (
                      file.kind
                    ) : (
                      <span className="inline-flex items-center gap-1">
                        <AlertCircle
                          className="h-4 w-4 shrink-0"
                          aria-hidden="true"
                        />
                        {NOTE_CHAT_FILE_STATUS_TEXT[status]}
                      </span>
                    )}
                  </span>
                </span>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="pointer-coarse:min-h-11 pointer-coarse:min-w-11 shrink-0 rounded-full"
                  aria-label={`Remove ${file.fileName}`}
                  data-note-file-remove=""
                  disabled={disabled}
                  onClick={() => {
                    focusAfterRemoval.current = index
                    onRemove(file.id)
                  }}
                >
                  <X className="h-4 w-4" aria-hidden="true" />
                </Button>
              </li>
            ))}
          </ul>
        </section>
      ) : (
        // Every file removed: say so, so the focus the last removal hands
        // this region lands on something visible.
        <p
          data-testid="workspace-action-note-files-none"
          className="mt-3 text-sm text-text-secondary"
        >
          No files from this chat will be saved with the note.
        </p>
      )}
    </div>
  )
}
